import {openConfirmDialog, type NavigateWithoutBlocker} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter, SettingsTemplate} from '@dms/settings/shared';
import type {SectionTab} from '@dms/shared';

import {CHECKOUT_PROFILE_TAB_IDS} from '../../../constants/CHECKOUT_PROFILE_TAB_IDS';
import {
  useCheckoutProfileFormState,
  type CheckoutProfileFormStateOptions,
} from '../hooks/useCheckoutProfileFormState';
import {CheckoutProfileDeposit} from './CheckoutProfileDeposit';
import {CheckoutProfileDocuments} from './CheckoutProfileDocuments';
import {CheckoutProfileFeedback} from './CheckoutProfileFeedback';
import {CheckoutProfileGeneral} from './CheckoutProfileGeneral';
import {CheckoutProfileIssueFocus} from './CheckoutProfileIssueFocus';
import {CheckoutProfilePayments} from './CheckoutProfilePayments';
import {CheckoutProfileRules} from './CheckoutProfileRules';

interface Props extends CheckoutProfileFormStateOptions {
  currency?: string;
  navigateWithoutBlocker: NavigateWithoutBlocker;
  onReload: () => void;
}

// Tabs that an internal sale does not show.
const EXTERNAL_SALE_TAB_IDS: string[] = [
  CHECKOUT_PROFILE_TAB_IDS.payments,
  CHECKOUT_PROFILE_TAB_IDS.deposit,
];

export function CheckoutProfileFormContent(props: Props) {
  const {
    values,
    status,
    issues,
    fieldErrors,
    typeOfSale,
    isDepositAllowed,
    activeDocumentRegime,
    setDocumentRegime,
    navigation,
    setNavigation,
    setTabId,
    getNavigationTarget,
    handleSelectIssue,
  } = useCheckoutProfileFormState(props);

  // The reload replaces the whole draft with the stored profile, so a dirty form asks first.
  const handleReload = () => {
    if (!props.formApi.formState.isDirty) {
      props.onReload();
      return;
    }

    openConfirmDialog({
      text: i18n.t('entity.checkout.labels.confirmProfileReload'),
      onConfirm: props.onReload,
    });
  };

  const tabs: SectionTab[] = [
    {
      queryId: CHECKOUT_PROFILE_TAB_IDS.general,
      title: i18n.t('general.labels.general'),
      icon: status.tabs.GENERAL,
      content: (
        <CheckoutProfileGeneral
          control={props.control}
          formApi={props.formApi}
          isCreating={isNil(props.profile)}
          currency={props.profile?.baseCurrency ?? props.currency}
          fieldErrors={fieldErrors}
        />
      ),
    },
    {
      queryId: CHECKOUT_PROFILE_TAB_IDS.payments,
      title: i18n.t('page.accounting.invoiceDetailPayments.title'),
      icon: status.tabs.PAYMENTS,
      content: (
        <CheckoutProfilePayments
          control={props.control}
          formApi={props.formApi}
          currency={props.profile?.baseCurrency ?? props.currency}
          fieldErrors={fieldErrors}
        />
      ),
    },
    {
      queryId: CHECKOUT_PROFILE_TAB_IDS.deposit,
      title: i18n.t('entity.order.labels.deposit'),
      icon: status.tabs.DEPOSIT,
      content: (
        <CheckoutProfileDeposit
          control={props.control}
          formApi={props.formApi}
          fieldErrors={fieldErrors}
        />
      ),
    },
    {
      queryId: CHECKOUT_PROFILE_TAB_IDS.documents,
      title: i18n.t('general.labels.documents'),
      icon: status.tabs.DOCUMENTS,
      content: (
        <CheckoutProfileDocuments
          profile={values}
          control={props.control}
          formApi={props.formApi}
          regimeStatuses={status.regimes}
          regime={activeDocumentRegime}
          onRegimeChange={setDocumentRegime}
          fieldErrors={fieldErrors}
          issues={issues.filter((issue) => issue.tab === 'DOCUMENTS')}
          onSelectIssue={handleSelectIssue}
        />
      ),
    },
    {
      queryId: CHECKOUT_PROFILE_TAB_IDS.assignmentRules,
      title: i18n.t('entity.automationRule.list.title'),
      isDisabled: isNil(props.profile),
      content: (
        <Show when={props.profile}>{(profile) => <CheckoutProfileRules profile={profile} />}</Show>
      ),
    },
  ];

  return (
    <SettingsTemplate
      header={{
        title: props.profile?.name ?? i18n.t('entity.checkout.labels.newCheckoutProfile'),
        breadcrumbs: [
          {label: i18n.t('page.settings.labels.profiles'), href: settingsRoutes.checkoutProfiles},
        ],
      }}
      tabs={tabs
        .filter(
          (tab) =>
            (typeOfSale !== 'INTERNAL' || !EXTERNAL_SALE_TAB_IDS.includes(tab.queryId)) &&
            (tab.queryId !== CHECKOUT_PROFILE_TAB_IDS.deposit || isDepositAllowed === true)
        )
        .map((tab) => ({
          ...tab,
          content: (
            <>
              <CheckoutProfileFeedback
                issues={issues.filter((issue) => {
                  const issueTab =
                    issue.tab?.toLowerCase() ??
                    props.serverResponse?.tab ??
                    CHECKOUT_PROFILE_TAB_IDS.general;

                  return (
                    issueTab === tab.queryId &&
                    (tab.queryId !== CHECKOUT_PROFILE_TAB_IDS.documents ||
                      (isNil(issue.saleRegime) && isNil(issue.documentType))) &&
                    !(
                      issue.code === 'LOCAL_DOCUMENTS' &&
                      typeOfSale !== 'INTERNAL' &&
                      status.regimes[activeDocumentRegime] === 'success'
                    )
                  );
                })}
                isDirty={props.formApi.formState.isDirty}
                onSelect={handleSelectIssue}
                getNavigationTarget={getNavigationTarget}
                onReload={handleReload}
              />
              <Box id={`checkout-feedback-${tab.queryId}`} tabIndex={-1}>
                {tab.content}
              </Box>
              <Show when={navigation?.issue.tab?.toLowerCase() === tab.queryId ? navigation : null}>
                {(target) => (
                  <CheckoutProfileIssueFocus key={target.sequence} issue={target.issue} />
                )}
              </Show>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () => props.navigateWithoutBlocker(settingsRoutes.checkoutProfiles),
                  },
                  {
                    type: 'form-button',
                    control: props.control,
                    title: i18n.t('general.actions.saveChanges'),
                    buttonType: 'submit',
                  },
                ]}
              />
            </>
          ),
        }))}
      onTabChange={(tabId) => {
        setNavigation(undefined);
        props.withoutBlocker(() => setTabId(tabId));
      }}
      isCreating={!props.profile}
      data-testid="checkout-profile-detail"
    />
  );
}
